import {
  dateRangeFiltersFields,
  isDateRangeFilterApplied,
} from 'app/dashboard/team_activity/ActivityFilters/DateRangeFilter'
import {
  EnquirerActionsAppliedFilterRenderer,
  EnquirerActionsFilterBody,
  enquirerActionsFilterDataExtractor,
  isEnquirerActionsApplied,
} from 'app/features/teams/messages/table-filters/EnquirerActionsFilter'
import {
  isLeadSourceFilterApplied,
  isLeadSourceFilterFeatureNew,
  LeadFilterTriggerPlaceholder,
  LeadSourceFilterBody,
  leadsSourceAppliedFilterRenderer,
  leadsSourceDataExtractor,
} from 'app/features/teams/messages/table-filters/LeadsFilter'
import {
  generateListingStatusFilterData,
  isMgmtTypeFilterApplied,
  MgmtFilAppliedFilterRenderer,
  MgmtTypeFilterBody,
  mgmtTypeFilterDataExtractor,
} from 'app/features/teams/messages/table-filters/MgmtTypeActionsFilter'
import {
  isPodsApplied,
  podFilterDataExtractor,
  PodsAppliedFilterRenderer,
  PodsFilterBody,
} from 'app/features/teams/messages/table-filters/PodsFilter'
import {
  generatePropertyFilterData,
  isPropertyFilterApplied,
  PropertyAppliedFilterRenderer,
  PropertyFilterBody,
  propertyFilterDataExtractor,
} from 'app/features/teams/messages/table-filters/PropertyFilter'
import {
  isPropertyManagerApplied,
  PropertyManagerAppliedFilterRenderer,
  PropertyManagerFilterBody,
  propertyManagerFilterDataExtractor,
} from 'app/features/teams/messages/table-filters/PropertyManagerFilter'
import {
  isPropertyTypeApplied,
  PropertyTypeAppliedFilterRenderer,
  PropertyTypeFilterBody,
  propertyTypeFilterDataExtractor,
} from 'app/features/teams/messages/table-filters/PropertyTypeFilter'
import {
  isReadStatusFilterApplied,
  readStatusAppliedFilterRenderer,
  readStatusDataExtractor,
  ReadStatusFilterBody,
} from 'app/features/teams/messages/table-filters/ReadStatusFilter'
import { standardUTCFormat } from 'app/sm/helpers'

export const filtersIds = {
  property: 'property',
  manager: 'manager',
  propertyType: 'propertyType',
  enquirerActions: 'enquirerActions',
  readStatus: 'readStatus',
  leadSource: 'leadSource',
  pods: 'pods',
  dateRange: 'dateRange',
  mgmtType: 'mgmtType',
}

export const filtersInitialState = {
  filters: {
    [filtersIds.property]: {},
    [filtersIds.manager]: {},
    [filtersIds.propertyType]: {},
    [filtersIds.enquirerActions]: {},
    [filtersIds.readStatus]: {},
    [filtersIds.leadSource]: {},
    [filtersIds.pods]: {},
    [filtersIds.dateRange]: {
      [dateRangeFiltersFields.start]: null,
      [dateRangeFiltersFields.end]: null,
    },
    [filtersIds.mgmtType]: {},
  },
  search: '',
}

export const filtersAppliedChecker = {
  [filtersIds.property]: isPropertyFilterApplied,
  [filtersIds.manager]: isPropertyManagerApplied,
  [filtersIds.propertyType]: isPropertyTypeApplied,
  [filtersIds.enquirerActions]: isEnquirerActionsApplied,
  [filtersIds.readStatus]: isReadStatusFilterApplied,
  [filtersIds.leadSource]: isLeadSourceFilterApplied,
  [filtersIds.pods]: isPodsApplied,
  [filtersIds.dateRange]: isDateRangeFilterApplied,
  [filtersIds.mgmtType]: isMgmtTypeFilterApplied,
}

export const filtersNewFeaturedChecker = {
  [filtersIds.leadSource]: isLeadSourceFilterFeatureNew,
}

export const hasAppliedFilters = (filtersState) => {
  if (!filtersState) return false
  return Object.values(filtersIds).some((filterId) => {
    const filterValue = filtersState.filters[filterId]
    const filterAppliedChecker = filtersAppliedChecker[filterId]
    return filterAppliedChecker(filterValue)
  })
}

export const hasNewFeaturedFilters = () => {
  return Object.values(filtersIds).some((filterId) =>
    filtersNewFeaturedChecker[filterId]?.(),
  )
}

export const getFilters = (filtersState) => {
  const properties = propertyFilterDataExtractor(
    filtersState.filters[filtersIds.property],
  ).properties
  const managers = propertyManagerFilterDataExtractor(
    filtersState.filters[filtersIds.manager],
  ).managers
  const propertyType = propertyTypeFilterDataExtractor(
    filtersState.filters[filtersIds.propertyType],
  )
  const enquirerActions = enquirerActionsFilterDataExtractor(
    filtersState.filters[filtersIds.enquirerActions],
  ).actions
  const readStatus = readStatusDataExtractor(
    filtersState.filters[filtersIds.readStatus],
  ).statuses
  const leadSource = leadsSourceDataExtractor(
    filtersState.filters[filtersIds.leadSource],
  ).source
  const pods = podFilterDataExtractor(
    filtersState.filters[filtersIds.pods],
  ).pods
  // const dateFilterBy = filtersState.filters[filtersIds.dateFilterBy]

  const range = filtersState.filters[filtersIds.dateRange]?.dateRange
  const mgmtType = mgmtTypeFilterDataExtractor(
    filtersState.filters[filtersIds.mgmtType],
  )
  return {
    properties,
    managers,
    propertyType,
    enquirerActions,
    readStatus,
    source: leadSource,
    pods,
    range,
    mgmtType: mgmtType.mgmtType,
    listingStatus: mgmtType.listingStatus,
  }
}

export const generateSelectedFilters = ({ properties = [], listingStatus }) => {
  return {
    filters: {
      [filtersIds.property]: generatePropertyFilterData({ properties }),
      [filtersIds.mgmtType]: {
        listingStatus: generateListingStatusFilterData({ listingStatus }),
      },
    },
  }
}

const getRenderedFiltersConfig = (filterId, filtersValues, updateFilterFn) => {
  const filterValues = filtersValues[filterId]
  return {
    filterValues,
    isApplied: filtersAppliedChecker[filterId](filterValues),
    setFilterValues: (values) => updateFilterFn(filterId, values),
  }
}

export const buildRenderedFilterConfig = (
  filtersValues,
  updateFilterFn,
  { includeListingStatusFilter = false } = {},
) => {
  return {
    [filtersIds.property]: {
      AppliedFilterRenderer: PropertyAppliedFilterRenderer,
      FilterBody: PropertyFilterBody,
      filterTriggerPlaceholder: 'Property Address',
      appliedFilterTitle: 'Property',
      ...getRenderedFiltersConfig(
        filtersIds.property,
        filtersValues,
        updateFilterFn,
      ),
    },
    [filtersIds.manager]: {
      AppliedFilterRenderer: PropertyManagerAppliedFilterRenderer,
      FilterBody: PropertyManagerFilterBody,
      filterTriggerPlaceholder: 'User',
      appliedFilterTitle: 'User',
      ...getRenderedFiltersConfig(
        filtersIds.manager,
        filtersValues,
        updateFilterFn,
      ),
    },
    [filtersIds.pods]: {
      AppliedFilterRenderer: PodsAppliedFilterRenderer,
      FilterBody: PodsFilterBody,
      filterTriggerPlaceholder: 'Pod',
      appliedFilterTitle: 'Pod',
      ...getRenderedFiltersConfig(
        filtersIds.pods,
        filtersValues,
        updateFilterFn,
      ),
    },
    [filtersIds.propertyType]: {
      AppliedFilterRenderer: PropertyTypeAppliedFilterRenderer,
      FilterBody: PropertyTypeFilterBody,
      filterTriggerPlaceholder: 'Attributes',
      appliedFilterTitle: 'Attributes',
      ...getRenderedFiltersConfig(
        filtersIds.propertyType,
        filtersValues,
        updateFilterFn,
      ),
    },
    [filtersIds.enquirerActions]: {
      AppliedFilterRenderer: EnquirerActionsAppliedFilterRenderer,
      FilterBody: EnquirerActionsFilterBody,
      filterTriggerPlaceholder: 'Enquirer',
      appliedFilterTitle: 'Enquirer',
      ...getRenderedFiltersConfig(
        filtersIds.enquirerActions,
        filtersValues,
        updateFilterFn,
      ),
    },
    [filtersIds.readStatus]: {
      AppliedFilterRenderer: readStatusAppliedFilterRenderer,
      FilterBody: ReadStatusFilterBody,
      filterTriggerPlaceholder: 'Status',
      appliedFilterTitle: 'Status',
      ...getRenderedFiltersConfig(
        filtersIds.readStatus,
        filtersValues,
        updateFilterFn,
      ),
    },
    [filtersIds.leadSource]: {
      AppliedFilterRenderer: leadsSourceAppliedFilterRenderer,
      FilterBody: LeadSourceFilterBody,
      filterTriggerPlaceholder: 'Leads',
      FilterTriggerPlaceholderCmp: LeadFilterTriggerPlaceholder,
      appliedFilterTitle: 'Leads',
      ...getRenderedFiltersConfig(
        filtersIds.leadSource,
        filtersValues,
        updateFilterFn,
      ),
    },
    [filtersIds.dateRange]: {
      filterId: filtersIds.dateRange,
      startFieldName: dateRangeFiltersFields.start,
      endFieldName: dateRangeFiltersFields.end,
      format: standardUTCFormat,
      ...getRenderedFiltersConfig(
        filtersIds.dateRange,
        filtersValues,
        updateFilterFn,
      ),
    },
    [filtersIds.mgmtType]: {
      AppliedFilterRenderer: MgmtFilAppliedFilterRenderer,
      FilterBody: MgmtTypeFilterBody,
      filterTriggerPlaceholder: includeListingStatusFilter
        ? 'Property'
        : 'Management Type',
      appliedFilterTitle: includeListingStatusFilter
        ? 'Property'
        : 'Management Type',
      ...getRenderedFiltersConfig(
        filtersIds.mgmtType,
        filtersValues,
        updateFilterFn,
      ),
    },
  }
}
